import {
    Dispatch,
    ReactNode,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import moment from 'moment-timezone';

import { AgencyContext } from '../../App';
import { HatCriteriaState } from '../Criteria/HatCriteria';
import { Metric } from '../../types/Analytics/Metric';
import { useHatCriteria } from '../Criteria/HatCriteria';
import { UseTabsState, useTabs } from '../Tabs';
import { setupMetricChange } from '../../components/Metrics/MetricCard';
import ApiService from '../../ApiService';

import { QueryMap } from '../../services/trialfire/types/QueryMap';
import { QueryCriteria, useQuery } from '../../services/trialfire/hooks/Query';
import { QueryRegistryKey } from '../../services/trialfire/hooks/QueryRegistry';
import { UseQueriesState, useQueries } from '../../services/trialfire/hooks/Queries';
import { UsePreviousQueriesState, usePreviousQueries } from '../../services/trialfire/hooks/Queries';

/**
 * Hat Metrics
 *
 */

interface HatMetrics {
    TOTAL_UNIQUE_TRAFFIC?: Metric;
    TOTAL_AD_INFLUENCED_TRAFFIC?: Metric;
    TOTAL_CONVERSIONS?: Metric;
    TOTAL_AD_INFLUENCED_CONVERSIONS?: Metric;
}

interface UseHatMetricsProps {
    queries: Partial<QueryMap>;
    previousQueries: Partial<QueryMap>;
}

const useHatMetrics = ({ queries, previousQueries }: UseHatMetricsProps) => {
    const calculateMetricData = useCallback(
        (sources: QueryRegistryKey[]) => {
            const value = sources.reduce((total, type) => total + (queries[type]?.data?.data ?? 0), 0);
            const previousValue = sources.reduce((total, type) => total + (previousQueries[type]?.data?.data ?? 0), 0);
            const isLoading = sources.some(
                (type) =>
                    (queries[type]?.isLoading && queries[type]?.isFetching) ||
                    (previousQueries[type]?.isLoading && previousQueries[type]?.isFetching)
            );

            return { value, previousValue, isLoading };
        },
        [queries, previousQueries]
    );

    const rawMetrics = useMemo(() => {
        const uniqueTrafficSources: QueryRegistryKey[] = ['TOTAL_UNIQUE_TRAFFIC_TOTAL_VISITS'];

        const adInfluencedTrafficSources: QueryRegistryKey[] = [
            'TOTAL_AD_INFLUENCED_TRAFFIC_TOTAL_VISITS',
            'TOTAL_AD_INFLUENCED_TRAFFIC_DIRECT',
            'TOTAL_AD_INFLUENCED_TRAFFIC_ORGANIC_SEARCH',
            'TOTAL_AD_INFLUENCED_TRAFFIC_PAID_SEARCH',
            'TOTAL_AD_INFLUENCED_TRAFFIC_REFERRAL',
            'TOTAL_AD_INFLUENCED_TRAFFIC_SOCIAL',
        ];

        const conversionsSources: QueryRegistryKey[] = [
            'TOTAL_CONVERSIONS_CLICK_TO_CALLS',
            'TOTAL_CONVERSIONS_IDENTIFIED_VISITORS',
            'TOTAL_CONVERSIONS_SERVICE_PAGE_VIEWS',
            'TOTAL_CONVERSIONS_SPECIALS_PAGE_VISITS',
            'TOTAL_CONVERSIONS_SRP_VIEWS',
            'TOTAL_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS',
            'TOTAL_CONVERSIONS_VDP_VIEWS',
        ];

        const adInfluencedConversionsSources: QueryRegistryKey[] = [
            'TOTAL_AD_INFLUENCED_CONVERSIONS_CLICK_TO_CALLS',
            'TOTAL_AD_INFLUENCED_CONVERSIONS_IDENTIFIED_VISITORS',
            'TOTAL_AD_INFLUENCED_CONVERSIONS_SERVICE_PAGE_VIEWS',
            'TOTAL_AD_INFLUENCED_CONVERSIONS_SPECIALS_PAGE_VISITS',
            'TOTAL_AD_INFLUENCED_CONVERSIONS_SRP_VIEWS',
            'TOTAL_AD_INFLUENCED_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS',
            'TOTAL_AD_INFLUENCED_CONVERSIONS_VDP_VIEWS',
        ];

        return {
            TOTAL_UNIQUE_TRAFFIC: calculateMetricData(uniqueTrafficSources),
            TOTAL_AD_INFLUENCED_TRAFFIC: calculateMetricData(adInfluencedTrafficSources),
            TOTAL_CONVERSIONS: calculateMetricData(conversionsSources),
            TOTAL_AD_INFLUENCED_CONVERSIONS: calculateMetricData(adInfluencedConversionsSources),
        };
    }, [calculateMetricData]);

    const [metrics, setMetrics] = useState<HatMetrics>({});

    useEffect(() => {
        setMetrics({
            TOTAL_UNIQUE_TRAFFIC: setupMetricChange({
                format: 'number',
                value: rawMetrics.TOTAL_UNIQUE_TRAFFIC.value,
                previousValue: rawMetrics.TOTAL_UNIQUE_TRAFFIC.previousValue,
                isLoading: rawMetrics.TOTAL_UNIQUE_TRAFFIC.isLoading,
                changePeriodLabel: 'Last Month',
            }),
            TOTAL_AD_INFLUENCED_TRAFFIC: setupMetricChange({
                format: 'number',
                value: rawMetrics.TOTAL_AD_INFLUENCED_TRAFFIC.value,
                previousValue: rawMetrics.TOTAL_AD_INFLUENCED_TRAFFIC.previousValue,
                isLoading: rawMetrics.TOTAL_AD_INFLUENCED_TRAFFIC.isLoading,
                changePeriodLabel: 'Last Month',
            }),
            TOTAL_CONVERSIONS: setupMetricChange({
                format: 'number',
                value: rawMetrics.TOTAL_CONVERSIONS.value,
                previousValue: rawMetrics.TOTAL_CONVERSIONS.previousValue,
                isLoading: rawMetrics.TOTAL_CONVERSIONS.isLoading,
                changePeriodLabel: 'Last Month',
            }),
            TOTAL_AD_INFLUENCED_CONVERSIONS: setupMetricChange({
                format: 'number',
                value: rawMetrics.TOTAL_AD_INFLUENCED_CONVERSIONS.value,
                previousValue: rawMetrics.TOTAL_AD_INFLUENCED_CONVERSIONS.previousValue,
                isLoading: rawMetrics.TOTAL_AD_INFLUENCED_CONVERSIONS.isLoading,
                changePeriodLabel: 'Last Month',
            }),
        });
    }, [rawMetrics]);

    return { metrics, setMetrics };
};

/**
 * Hat Dashboard Page Context
 *
 */

type HatDashboardPageContextValue = HatCriteriaState &
    UseQueriesState &
    UsePreviousQueriesState & {
        tabs: UseTabsState;
        mediaTabs: UseTabsState;

        defaultMinDate: Date;
        defaultMaxDate: Date;
        defaultStartDate: Date;
        defaultEndDate: Date;

        metrics: HatMetrics;
        setMetrics: Dispatch<SetStateAction<HatMetrics>>;
    };

const HatDashboardPageContext = createContext<HatDashboardPageContextValue | undefined>(undefined);

const useHatDashboardPageContext = (): HatDashboardPageContextValue => {
    const context: HatDashboardPageContextValue | undefined = useContext(HatDashboardPageContext);
    if (!context) {
        throw new Error('useHatDashboardPageContext must be used within a HatDashboardPageProvider');
    }
    return context;
};

/**
 * Hat Dashboard Page Provider
 *
 */

interface HatDashboardPageProviderProps {
    children: ReactNode;
    value: HatDashboardPageContextValue;
}

const HatDashboardPageProvider = ({ children, value }: HatDashboardPageProviderProps) => {
    return <HatDashboardPageContext.Provider value={value}>{children}</HatDashboardPageContext.Provider>;
};

/**
 * Hat Dashboard Page Hook
 *
 */

const useHatDashboardPage = (): HatDashboardPageContextValue => {
    const agency = useContext(AgencyContext);
    const agencyId: number = agency?.id as number;

    const tabs: UseTabsState = useTabs();
    const mediaTabs: UseTabsState = useTabs();

    let defaultMinDate = new Date(2020, 0, 1);
    let defaultMaxDate: Date = moment().subtract(1, 'days').startOf('day').toDate();
    let defaultStartDate: Date = moment().subtract(30, 'days').startOf('day').toDate();
    let defaultEndDate: Date = defaultMaxDate;

    const $hatCriteria: HatCriteriaState = useHatCriteria({
        hatCriteria: {
            mediaType: '',
            agencyId: agencyId > 0 ? agencyId : null,
            advertiserId: null,
            startDate: defaultStartDate,
            endDate: defaultEndDate,

            previousStartDate: defaultStartDate,
            previousEndDate: defaultStartDate,
        },
    });
    const { hatCriteria } = $hatCriteria;

    const [queryCriteria, setQueryCriteria] = useState<QueryCriteria>({
        apiToken: '',
        mediaType: '',
        range: {
            startDate: moment(defaultStartDate).valueOf(),
            endDate: moment(defaultStartDate).valueOf(),
            period: 'daily',
        },
    });

    const [previousQueryCriteria, setPreviousQueryCriteria] = useState<QueryCriteria>({
        apiToken: '',
        mediaType: '',
        range: {
            startDate: moment(defaultStartDate).valueOf(),
            endDate: moment(defaultStartDate).valueOf(),
            period: 'daily',
        },
    });

    const { queries, setQueries } = useQueries();
    const { previousQueries, setPreviousQueries } = usePreviousQueries();

    const { metrics, setMetrics } = useHatMetrics({ queries, previousQueries });

    useEffect(() => {
        if (hatCriteria.advertiserId) {
            ApiService.getDealer(hatCriteria.advertiserId).then((response) => {
                const { data } = response;

                setQueryCriteria((prev) => ({
                    ...prev,
                    apiToken: data?.tfApiToken || '',
                }));

                setPreviousQueryCriteria((prev) => ({
                    ...prev,
                    apiToken: data?.tfApiToken || '',
                }));
            });
        } else {
            setQueryCriteria((prev) => ({
                ...prev,
                apiToken: '',
            }));

            setPreviousQueryCriteria((prev) => ({
                ...prev,
                apiToken: '',
            }));
        }
    }, [hatCriteria.advertiserId]);

    useEffect(() => {
        setQueryCriteria((prev) => ({
            ...prev,
            mediaType: hatCriteria.mediaType,
            range: {
                ...prev.range,
                startDate: moment(hatCriteria.startDate).valueOf(),
                endDate: moment(hatCriteria.endDate).endOf('day').valueOf(),
            },
            advertiserId: hatCriteria.advertiserId || undefined,
            advertiser: hatCriteria.advertiser || null,
        }));

        setPreviousQueryCriteria((prev) => ({
            ...prev,
            mediaType: hatCriteria.mediaType,
            range: {
                ...prev.range,
                startDate: moment(hatCriteria.previousStartDate).valueOf(),
                endDate: moment(hatCriteria.previousEndDate).endOf('day').valueOf(),
            },
            advertiserId: hatCriteria.advertiserId || undefined,
            advertiser: hatCriteria.advertiser || null,
        }));
    }, [hatCriteria]);

    const _queries = {
        TOTAL_AD_INFLUENCED_CONVERSIONS_CLICK_TO_CALLS: useQuery(
            'TOTAL_AD_INFLUENCED_CONVERSIONS_CLICK_TO_CALLS',
            queryCriteria
        ),
        TOTAL_AD_INFLUENCED_CONVERSIONS_IDENTIFIED_VISITORS: useQuery(
            'TOTAL_AD_INFLUENCED_CONVERSIONS_IDENTIFIED_VISITORS',
            queryCriteria
        ),
        TOTAL_AD_INFLUENCED_CONVERSIONS_SERVICE_PAGE_VIEWS: useQuery(
            'TOTAL_AD_INFLUENCED_CONVERSIONS_SERVICE_PAGE_VIEWS',
            queryCriteria
        ),
        TOTAL_AD_INFLUENCED_CONVERSIONS_SPECIALS_PAGE_VISITS: useQuery(
            'TOTAL_AD_INFLUENCED_CONVERSIONS_SPECIALS_PAGE_VISITS',
            queryCriteria
        ),
        TOTAL_AD_INFLUENCED_CONVERSIONS_SRP_VIEWS: useQuery('TOTAL_AD_INFLUENCED_CONVERSIONS_SRP_VIEWS', queryCriteria),
        TOTAL_AD_INFLUENCED_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS: useQuery(
            'TOTAL_AD_INFLUENCED_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS',
            queryCriteria
        ),
        TOTAL_AD_INFLUENCED_CONVERSIONS_VDP_VIEWS: useQuery('TOTAL_AD_INFLUENCED_CONVERSIONS_VDP_VIEWS', queryCriteria),
        TOTAL_AD_INFLUENCED_CONVERSIONS: useQuery('TOTAL_AD_INFLUENCED_CONVERSIONS', queryCriteria),
        TOTAL_AD_INFLUENCED_TRAFFIC_DIRECT: useQuery('TOTAL_AD_INFLUENCED_TRAFFIC_DIRECT', queryCriteria),
        TOTAL_AD_INFLUENCED_TRAFFIC_ICAN: useQuery('TOTAL_AD_INFLUENCED_TRAFFIC_ICAN', queryCriteria),
        TOTAL_AD_INFLUENCED_TRAFFIC_ORGANIC_SEARCH: useQuery(
            'TOTAL_AD_INFLUENCED_TRAFFIC_ORGANIC_SEARCH',
            queryCriteria
        ),
        TOTAL_AD_INFLUENCED_TRAFFIC_PAID_SEARCH: useQuery('TOTAL_AD_INFLUENCED_TRAFFIC_PAID_SEARCH', queryCriteria),
        TOTAL_AD_INFLUENCED_TRAFFIC_REFERRAL: useQuery('TOTAL_AD_INFLUENCED_TRAFFIC_REFERRAL', queryCriteria),
        TOTAL_AD_INFLUENCED_TRAFFIC_SOCIAL: useQuery('TOTAL_AD_INFLUENCED_TRAFFIC_SOCIAL', queryCriteria),
        TOTAL_AD_INFLUENCED_TRAFFIC_TOTAL_VISITS: useQuery('TOTAL_AD_INFLUENCED_TRAFFIC_TOTAL_VISITS', queryCriteria),
        TOTAL_AD_INFLUENCED_TRAFFIC: useQuery('TOTAL_AD_INFLUENCED_TRAFFIC', queryCriteria),
        TOTAL_CONVERSIONS_AMAZON_DIRECT: useQuery('TOTAL_CONVERSIONS_AMAZON_DIRECT', queryCriteria),
        TOTAL_CONVERSIONS_CLICK_TO_CALLS: useQuery('TOTAL_CONVERSIONS_CLICK_TO_CALLS', queryCriteria),
        TOTAL_CONVERSIONS_DIRECT: useQuery('TOTAL_CONVERSIONS_DIRECT', queryCriteria),
        TOTAL_CONVERSIONS_IDENTIFIED_VISITORS: useQuery('TOTAL_CONVERSIONS_IDENTIFIED_VISITORS', queryCriteria),
        TOTAL_CONVERSIONS_ORGANIC: useQuery('TOTAL_CONVERSIONS_ORGANIC', queryCriteria),
        TOTAL_CONVERSIONS_PAID_SEARCH: useQuery('TOTAL_CONVERSIONS_PAID_SEARCH', queryCriteria),
        TOTAL_CONVERSIONS_REFERRAL: useQuery('TOTAL_CONVERSIONS_REFERRAL', queryCriteria),
        TOTAL_CONVERSIONS_SERVICE_PAGE_VIEWS: useQuery('TOTAL_CONVERSIONS_SERVICE_PAGE_VIEWS', queryCriteria),
        TOTAL_CONVERSIONS_SOCIAL: useQuery('TOTAL_CONVERSIONS_SOCIAL', queryCriteria),
        TOTAL_CONVERSIONS_SPECIALS_PAGE_VISITS: useQuery('TOTAL_CONVERSIONS_SPECIALS_PAGE_VISITS', queryCriteria),
        TOTAL_CONVERSIONS_SRP_VIEWS: useQuery('TOTAL_CONVERSIONS_SRP_VIEWS', queryCriteria),
        TOTAL_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS: useQuery(
            'TOTAL_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS',
            queryCriteria
        ),
        TOTAL_CONVERSIONS_VDP_VIEWS: useQuery('TOTAL_CONVERSIONS_VDP_VIEWS', queryCriteria),
        TOTAL_CONVERSIONS: useQuery('TOTAL_CONVERSIONS', queryCriteria),
        TOTAL_UNIQUE_TRAFFIC_DIRECT: useQuery('TOTAL_UNIQUE_TRAFFIC_DIRECT', queryCriteria),
        TOTAL_UNIQUE_TRAFFIC_ICAN: useQuery('TOTAL_UNIQUE_TRAFFIC_ICAN', queryCriteria),
        TOTAL_UNIQUE_TRAFFIC_ORGANIC_SEARCH: useQuery('TOTAL_UNIQUE_TRAFFIC_ORGANIC_SEARCH', queryCriteria),
        TOTAL_UNIQUE_TRAFFIC_PAID_SEARCH: useQuery('TOTAL_UNIQUE_TRAFFIC_PAID_SEARCH', queryCriteria),
        TOTAL_UNIQUE_TRAFFIC_REFERRAL: useQuery('TOTAL_UNIQUE_TRAFFIC_REFERRAL', queryCriteria),
        TOTAL_UNIQUE_TRAFFIC_SOCIAL: useQuery('TOTAL_UNIQUE_TRAFFIC_SOCIAL', queryCriteria),
        TOTAL_UNIQUE_TRAFFIC_TOTAL_VISITS: useQuery('TOTAL_UNIQUE_TRAFFIC_TOTAL_VISITS', queryCriteria),
        TOTAL_UNIQUE_TRAFFIC: useQuery('TOTAL_UNIQUE_TRAFFIC', queryCriteria),

        TREND_IMPRESSIONS: useQuery('TREND_IMPRESSIONS', queryCriteria),
        TREND_VISITS: useQuery('TREND_VISITS', queryCriteria),

        // TREND_IMPRESSIONS_VERSUS_VISITS: useQuery('TREND_IMPRESSIONS_VERSUS_VISITS', queryCriteria),
    };

    useEffect(() => {
        setQueries(_queries);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setQueries, ...Object.values(_queries).map((query) => query?.data)]);

    const _previousQueries = {
        TOTAL_AD_INFLUENCED_CONVERSIONS_CLICK_TO_CALLS: useQuery(
            'TOTAL_AD_INFLUENCED_CONVERSIONS_CLICK_TO_CALLS',
            previousQueryCriteria
        ),
        TOTAL_AD_INFLUENCED_CONVERSIONS_IDENTIFIED_VISITORS: useQuery(
            'TOTAL_AD_INFLUENCED_CONVERSIONS_IDENTIFIED_VISITORS',
            previousQueryCriteria
        ),
        TOTAL_AD_INFLUENCED_CONVERSIONS_SERVICE_PAGE_VIEWS: useQuery(
            'TOTAL_AD_INFLUENCED_CONVERSIONS_SERVICE_PAGE_VIEWS',
            previousQueryCriteria
        ),
        TOTAL_AD_INFLUENCED_CONVERSIONS_SPECIALS_PAGE_VISITS: useQuery(
            'TOTAL_AD_INFLUENCED_CONVERSIONS_SPECIALS_PAGE_VISITS',
            previousQueryCriteria
        ),
        TOTAL_AD_INFLUENCED_CONVERSIONS_SRP_VIEWS: useQuery(
            'TOTAL_AD_INFLUENCED_CONVERSIONS_SRP_VIEWS',
            previousQueryCriteria
        ),
        TOTAL_AD_INFLUENCED_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS: useQuery(
            'TOTAL_AD_INFLUENCED_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS',
            previousQueryCriteria
        ),
        TOTAL_AD_INFLUENCED_CONVERSIONS_VDP_VIEWS: useQuery(
            'TOTAL_AD_INFLUENCED_CONVERSIONS_VDP_VIEWS',
            previousQueryCriteria
        ),
        TOTAL_AD_INFLUENCED_CONVERSIONS: useQuery('TOTAL_AD_INFLUENCED_CONVERSIONS', previousQueryCriteria),
        TOTAL_AD_INFLUENCED_TRAFFIC_DIRECT: useQuery('TOTAL_AD_INFLUENCED_TRAFFIC_DIRECT', previousQueryCriteria),
        TOTAL_AD_INFLUENCED_TRAFFIC_ICAN: useQuery('TOTAL_AD_INFLUENCED_TRAFFIC_ICAN', previousQueryCriteria),
        TOTAL_AD_INFLUENCED_TRAFFIC_ORGANIC_SEARCH: useQuery(
            'TOTAL_AD_INFLUENCED_TRAFFIC_ORGANIC_SEARCH',
            previousQueryCriteria
        ),
        TOTAL_AD_INFLUENCED_TRAFFIC_PAID_SEARCH: useQuery(
            'TOTAL_AD_INFLUENCED_TRAFFIC_PAID_SEARCH',
            previousQueryCriteria
        ),
        TOTAL_AD_INFLUENCED_TRAFFIC_REFERRAL: useQuery('TOTAL_AD_INFLUENCED_TRAFFIC_REFERRAL', previousQueryCriteria),
        TOTAL_AD_INFLUENCED_TRAFFIC_SOCIAL: useQuery('TOTAL_AD_INFLUENCED_TRAFFIC_SOCIAL', previousQueryCriteria),
        TOTAL_AD_INFLUENCED_TRAFFIC_TOTAL_VISITS: useQuery(
            'TOTAL_AD_INFLUENCED_TRAFFIC_TOTAL_VISITS',
            previousQueryCriteria
        ),
        TOTAL_AD_INFLUENCED_TRAFFIC: useQuery('TOTAL_AD_INFLUENCED_TRAFFIC', previousQueryCriteria),
        TOTAL_CONVERSIONS_AMAZON_DIRECT: useQuery('TOTAL_CONVERSIONS_AMAZON_DIRECT', previousQueryCriteria),
        TOTAL_CONVERSIONS_CLICK_TO_CALLS: useQuery('TOTAL_CONVERSIONS_CLICK_TO_CALLS', previousQueryCriteria),
        TOTAL_CONVERSIONS_DIRECT: useQuery('TOTAL_CONVERSIONS_DIRECT', previousQueryCriteria),
        TOTAL_CONVERSIONS_IDENTIFIED_VISITORS: useQuery('TOTAL_CONVERSIONS_IDENTIFIED_VISITORS', previousQueryCriteria),
        TOTAL_CONVERSIONS_ORGANIC: useQuery('TOTAL_CONVERSIONS_ORGANIC', previousQueryCriteria),
        TOTAL_CONVERSIONS_PAID_SEARCH: useQuery('TOTAL_CONVERSIONS_PAID_SEARCH', previousQueryCriteria),
        TOTAL_CONVERSIONS_REFERRAL: useQuery('TOTAL_CONVERSIONS_REFERRAL', previousQueryCriteria),
        TOTAL_CONVERSIONS_SERVICE_PAGE_VIEWS: useQuery('TOTAL_CONVERSIONS_SERVICE_PAGE_VIEWS', previousQueryCriteria),
        TOTAL_CONVERSIONS_SOCIAL: useQuery('TOTAL_CONVERSIONS_SOCIAL', previousQueryCriteria),
        TOTAL_CONVERSIONS_SPECIALS_PAGE_VISITS: useQuery(
            'TOTAL_CONVERSIONS_SPECIALS_PAGE_VISITS',
            previousQueryCriteria
        ),
        TOTAL_CONVERSIONS_SRP_VIEWS: useQuery('TOTAL_CONVERSIONS_SRP_VIEWS', previousQueryCriteria),
        TOTAL_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS: useQuery(
            'TOTAL_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS',
            previousQueryCriteria
        ),
        TOTAL_CONVERSIONS_VDP_VIEWS: useQuery('TOTAL_CONVERSIONS_VDP_VIEWS', previousQueryCriteria),
        TOTAL_CONVERSIONS: useQuery('TOTAL_CONVERSIONS', previousQueryCriteria),
        TOTAL_UNIQUE_TRAFFIC_DIRECT: useQuery('TOTAL_UNIQUE_TRAFFIC_DIRECT', previousQueryCriteria),
        TOTAL_UNIQUE_TRAFFIC_ICAN: useQuery('TOTAL_UNIQUE_TRAFFIC_ICAN', previousQueryCriteria),
        TOTAL_UNIQUE_TRAFFIC_ORGANIC_SEARCH: useQuery('TOTAL_UNIQUE_TRAFFIC_ORGANIC_SEARCH', previousQueryCriteria),
        TOTAL_UNIQUE_TRAFFIC_PAID_SEARCH: useQuery('TOTAL_UNIQUE_TRAFFIC_PAID_SEARCH', previousQueryCriteria),
        TOTAL_UNIQUE_TRAFFIC_REFERRAL: useQuery('TOTAL_UNIQUE_TRAFFIC_REFERRAL', previousQueryCriteria),
        TOTAL_UNIQUE_TRAFFIC_SOCIAL: useQuery('TOTAL_UNIQUE_TRAFFIC_SOCIAL', previousQueryCriteria),
        TOTAL_UNIQUE_TRAFFIC_TOTAL_VISITS: useQuery('TOTAL_UNIQUE_TRAFFIC_TOTAL_VISITS', previousQueryCriteria),
        TOTAL_UNIQUE_TRAFFIC: useQuery('TOTAL_UNIQUE_TRAFFIC', previousQueryCriteria),
    };

    useEffect(() => {
        setPreviousQueries(_previousQueries);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setPreviousQueries, ...Object.values(_previousQueries).map((query) => query?.data)]);
    // }, [setPreviousQueries]);

    return {
        defaultMinDate,
        defaultMaxDate,
        defaultStartDate,
        defaultEndDate,

        ...$hatCriteria,

        tabs,
        mediaTabs,

        metrics,
        setMetrics,

        queries,
        setQueries,

        previousQueries,
        setPreviousQueries,
    };
};

export type { HatDashboardPageContextValue };
export { HatDashboardPageContext };
export { useHatDashboardPageContext };

export type { HatDashboardPageProviderProps };
export { HatDashboardPageProvider };

export { useHatDashboardPage };
